// PermissionsPage.js
import React, { useEffect, useState } from "react";
import { getDatabase, ref, get, update } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";

function PermissionsPage() {
  const [userUID, setUserUID] = useState(null);
  const [devices, setDevices] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [visibleCols, setVisibleCols] = useState({ userName: false, userAddress: false, group: false });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [bulkType, setBulkType] = useState("fire");
  const [bulkValue, setBulkValue] = useState(true);
  const [mode, setMode] = useState("send"); // "send" or "receive"
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const toggleCol = (col) => {
    setVisibleCols((prev) => ({ ...prev, [col]: !prev[col] }));
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserUID(user.uid);
      } else {
        setUserUID(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!userUID) return;

    const db = getDatabase();
    const userMapRef = ref(db, `user_esp32_map/${userUID}`);
    const indexRef = ref(db, `esp32_index`);

    Promise.all([get(userMapRef), get(indexRef)]).then(([userSnap, indexSnap]) => {
      if (userSnap.exists() && indexSnap.exists()) {
        const userData = userSnap.val();
        const indexData = indexSnap.val();

        const shortIDs = Object.keys(userData);
        const validIDs = shortIDs.filter((id) => indexData[id]);
        setDevices(validIDs);

        const permissionPromises = validIDs.map((id) => {
          const group = indexData[id];
          return get(ref(db, `esp32_members/${group}/${id}`)).then((snap) => {
            const data = snap.val();
            return {
              id,
              group,
              userName: data?.userName || "-",
              userAddress: data?.userAddress || "-",
              canSendTypes: data?.canSendTypes || { fire: false, help: false, evacuate: false },
              canReciveTypes: data?.canReciveTypes || { fire: false, help: false, evacuate: false },
            };
          });
        });

        Promise.all(permissionPromises).then((results) => {
          const perms = {};
          results.forEach(({ id, group, userName, userAddress, canSendTypes, canReciveTypes }) => {
            perms[id] = {
              canSendTypes,
              canReciveTypes,
              group,
              userName,
              userAddress
            };
          });
          setPermissions(perms);
          setLoading(false);
        });
      }
    });
  }, [userUID]);

  const togglePermission = (id, type) => {
    const db = getDatabase();
    const group = permissions[id]?.group;
    if (!group) return;

    const current = permissions[id]?.[mode === "send" ? "canSendTypes" : "canReciveTypes"]?.[type] || false;
    const updated = !current;

    const path = `esp32_members/${group}/${id}/${mode === "send" ? "canSendTypes" : "canReciveTypes"}/${type}`;
    update(ref(db), { [path]: updated });

    setPermissions((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [mode === "send" ? "canSendTypes" : "canReciveTypes"]: {
          ...prev[id][mode === "send" ? "canSendTypes" : "canReciveTypes"],
          [type]: updated,
        },
      },
    }));
  };

  const getIcon = (type) => {
      switch (type) {
        case "fire": return "🔥";
        case "help": return "🆘";
        case "evacuate": return "🚨";
        default: return "";
      }
    };

  const filteredDevices = devices.filter((id) => {
    const lower = searchTerm.toLowerCase();
    const item = permissions[id];
    return (
      id.toLowerCase().includes(lower) ||
      (visibleCols.userName && item?.userName?.toLowerCase().includes(lower)) ||
      (visibleCols.userAddress && item?.userAddress?.toLowerCase().includes(lower)) ||
      (visibleCols.group && item?.group?.toLowerCase().includes(lower))
    );
  });

   const paginatedDevices = filteredDevices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const toggleSelect = (id) => {
    setSelectedDevices((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const applyBulkUpdate = () => {
    const db = getDatabase();
    const updates = {};
    selectedDevices.forEach((id) => {
      const group = permissions[id]?.group;
      if (group) {
        const path = `esp32_members/${group}/${id}/${mode === "send" ? "canSendTypes" : "canReciveTypes"}/${bulkType}`;
        updates[path] = bulkValue;
        permissions[id][mode === "send" ? "canSendTypes" : "canReciveTypes"][bulkType] = bulkValue;
      }
    });
    update(ref(db), updates);
    setPermissions({ ...permissions });
  };

  if (loading) return <div className="container mt-5">Loading...</div>;

  return (
    <div className="container mt-5">
      <div className="card shadow-lg">
        <div className="card-body">

          <div className="mb-4">
            <div className="card">
              <div className="card-body d-flex gap-3 align-items-center">
                <strong className="me-2">โหมด:</strong>
                <button className={`btn ${mode === "send" ? "btn-warning" : "btn-outline-warning"}`} onClick={() => setMode("send")}>ส่งแจ้งเตือน</button>
                <button className={`btn ${mode === "receive" ? "btn-info" : "btn-outline-info"}`} onClick={() => setMode("receive")}>รับแจ้งเตือน</button>
                
                
              </div>
            </div>
          </div>

          <h4 className="mb-3" style={{ marginTop: '1rem' }}>กำหนดสิทธิ-{mode === "send" ? "ส่งแจ้งเตือน" : "รับแจ้งเตือน"}</h4>

          <div className="d-flex flex-wrap gap-3 mb-3 align-items-center">
            <div className="form-check form-check-inline">
              <input type="checkbox" className="form-check-input" id="chk-name" checked={visibleCols.userName} onChange={() => toggleCol('userName')} />
              <label className="form-check-label" htmlFor="chk-name">ชื่อผู้ใช้</label>
            </div>
            <div className="form-check form-check-inline">
              <input type="checkbox" className="form-check-input" id="chk-addr" checked={visibleCols.userAddress} onChange={() => toggleCol('userAddress')} />
              <label className="form-check-label" htmlFor="chk-addr">ที่อยู่</label>
            </div>
            <div className="form-check form-check-inline">
              <input type="checkbox" className="form-check-input" id="chk-group" checked={visibleCols.group} onChange={() => toggleCol('group')} />
              <label className="form-check-label" htmlFor="chk-group">กลุ่ม</label>
            </div>
            <input type="text" className="form-control ms-auto" style={{ maxWidth: "300px" }} placeholder="ค้นหา..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>

          <div style={{ overflowX: 'auto' }}>
            <div className="d-flex flex-nowrap gap-2 align-items-center mb-3" style={{ minWidth: '500px' }}>
              <label className="me-2 mb-0">อัปเดตหลายเครื่อง:</label>
              <select className="form-select w-auto" value={bulkType} onChange={(e) => setBulkType(e.target.value)}>
                <option value="fire">เพลิงไหม้</option>
                <option value="help">ช่วยเหลือ</option>
                <option value="evacuate">อพยพ</option>
              </select>
              <select className="form-select w-auto" value={bulkValue.toString()} onChange={(e) => setBulkValue(e.target.value === "true")}>                
                <option value="true">ON</option>
                <option value="false">OFF</option>
              </select>
              <button className="btn btn-primary" onClick={applyBulkUpdate}>บันทึก</button>
              <span className="text-muted">เลือกแล้ว {selectedDevices.length} รายการ</span>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table
  className={`table table-bordered table-striped ${mode === "send" ? "table-warning" : "table-info"}`}
  style={{ minWidth: '100%', whiteSpace: 'nowrap' }}
>
              <thead className="table-dark">
                <tr>
                  <th><input type="checkbox" onChange={(e) => {
                    if (e.target.checked) setSelectedDevices(filteredDevices);
                    else setSelectedDevices([]);
                  }} /></th>
                  <th>รหัสเครื่อง (Short ID)</th>
                  {visibleCols.userName && <th>ชื่อผู้ใช้</th>}
                  {visibleCols.userAddress && <th>ที่อยู่</th>}
                  {visibleCols.group && <th>กลุ่ม</th>}
                  <th>เพลิงไหม้</th>
                  <th>ช่วยเหลือ</th>
                  <th>อพยพ</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDevices.map((id) => (
                  <tr key={id}>
                    <td><input type="checkbox" checked={selectedDevices.includes(id)} onChange={() => toggleSelect(id)} /></td>
                    <td>{id}</td>
                    {visibleCols.userName && <td>{permissions[id]?.userName}</td>}
                    {visibleCols.userAddress && <td>{permissions[id]?.userAddress}</td>}
                    {visibleCols.group && <td>{permissions[id]?.group}</td>}
                    {["fire", "help", "evacuate"].map((type) => {
                      const value = permissions[id]?.[mode === "send" ? "canSendTypes" : "canReciveTypes"]?.[type];
                      return (
                        <td key={type}>
                          <button
                            className={`btn btn-sm ${value ? 'btn-success' : 'btn-secondary'}`}
                            onClick={() => togglePermission(id, type)}
                          >
                            {getIcon(type)} {value ? 'ON' : 'OFF'}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {filteredDevices.length === 0 && (
                  <tr>
                    <td colSpan="10" className="text-center text-muted">ไม่มีข้อมูล</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="d-flex justify-content-center my-3">
            <button
              className="btn btn-outline-dark me-2"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >ย้อนกลับ</button>
            <span className="align-self-center">หน้า {currentPage} / {Math.ceil(filteredDevices.length / itemsPerPage)}</span>
            <button
              className="btn btn-outline-dark ms-2"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(filteredDevices.length / itemsPerPage)))}
              disabled={currentPage === Math.ceil(filteredDevices.length / itemsPerPage)}
            >ถัดไป</button>
          </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default PermissionsPage;
